/*
 * Farida Gupta - collection "Load more products" button (theme/sections/main-collection.liquid).
 *
 * Appends the next page's product cards to the existing grid instead of navigating away, using
 * Shopify's section rendering endpoint (?section_id=...) - the same mechanism theme.js's facets
 * facet:update listener uses - but merging the fetched markup into the live DOM instead of
 * replacing the section outright, since a "load more" click should add to the grid, not reset it.
 *
 * Deliberately independent of theme.js (plain custom element, no shared private state) - same
 * reasoning as fg-product-gallery.js: this only needs to react to its own click, not participate
 * in theme.js's bundled internals.
 *
 * Also backfills earlier pages on load when landing directly on ?page=N>1 (reload, back/forward,
 * shared link) - see backfillPreviousPages() below. Because of that, the section markup renders
 * this element (and loads this script) whenever paginate.pages > 1, not only when there's a next
 * page, so backfill still runs on the last page.
 *
 * A section can render more than one paginated list at once. Shopify's `page` URL param is global,
 * so every `{% paginate %}` in the section advances together, but each list still needs its OWN
 * element to update: pass `list-id` to scope an instance to `#<list-id>` instead of the default
 * `#product-list-<section-id>`, and to pick the matching <load-more-button> (by its own `list-id`)
 * out of a fetch that contains several.
 */
class LoadMoreButton extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    this.button?.addEventListener('click', () => this.loadMore());

    const currentPage = Number(this.getAttribute('current-page'));

    if (currentPage > 1) this.backfillPreviousPages(currentPage);
  }

  getListId() {
    return this.getAttribute('list-id') || `product-list-${this.getAttribute('section-id')}`;
  }

  // A hard reload/direct visit to a "load more"-d URL (e.g. ?page=3) only ever gets that single
  // page's products from Shopify's paginate tag - see loadMore()'s history.replaceState below for
  // why the URL says page 3 without the earlier pages actually having been rendered. This re-fetches
  // pages 1..current-1 via the same section-rendering endpoint and prepends them above the page
  // that was already server-rendered, so the grid matches what a "load more" session would show.
  async backfillPreviousPages(currentPage) {
    const sectionId = this.getAttribute('section-id');
    const listId = this.getListId();
    const currentProductList = document.getElementById(listId);

    if (!sectionId || !currentProductList) return;

    const pageNumbers = Array.from({ length: currentPage - 1 }, (_, index) => index + 1);

    const pages = await Promise.all(
      pageNumbers.map(async (pageNumber) => {
        try {
          const url = new URL(window.location.href);
          url.searchParams.set('section_id', sectionId);
          url.searchParams.set('page', String(pageNumber));

          const response = await fetch(url.toString());
          const html = await response.text();

          return new DOMParser().parseFromString(html, 'text/html').getElementById(listId);
        } catch (error) {
          return null;
        }
      })
    );

    // Anchor to insert before rather than repeated prepend(), so pages land in ascending order
    // (1, 2, ..., current - 1) even though their fetches can resolve out of order.
    const firstOriginalChild = currentProductList.firstChild;

    for (const pageProductList of pages) {
      if (!pageProductList) continue;

      const importedPageProductList = document.importNode(pageProductList, true);

      // Same reasoning as loadMore() below: these cards were already scrolled past before the
      // reload, so skip the one-shot reveal-on-scroll animation entirely.
      importedPageProductList.querySelectorAll('[reveal-on-scroll="true"]').forEach((element) => {
        element.removeAttribute('reveal-on-scroll');
      });

      [...importedPageProductList.childNodes].forEach((node) => {
        currentProductList.insertBefore(node, firstOriginalChild);
      });
    }

    // Backfilled cards carry their own #smartwishlist mount points (product-card.liquid) that the
    // Smart Wishlist app hasn't scanned yet - see fg-wishlist-reinit.js for why a plain reinit call
    // is needed here instead of relying on theme.js's theme:loading:end event.
    window.ReloadSmartWishlist?.();
  }

  async loadMore() {
    if (!this.button || this.button.disabled) return;

    const nextUrl = this.getAttribute('next-url');
    const sectionId = this.getAttribute('section-id');
    const listId = this.getListId();

    if (!nextUrl || !sectionId) return;

    const url = new URL(nextUrl, window.location.origin);
    url.searchParams.set('section_id', sectionId);

    this.button.disabled = true;
    this.button.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(url.toString());
      const html = await response.text();
      const parsedDocument = new DOMParser().parseFromString(html, 'text/html');

      const newProductList = parsedDocument.getElementById(listId);
      const currentProductList = document.getElementById(listId);

      if (newProductList && currentProductList) {
        const importedProductList = document.importNode(newProductList, true);

        // <product-list>'s reveal-on-scroll animation (theme.js) is a one-shot IntersectionObserver
        // set up once in its connectedCallback, which has already fired for currentProductList by the
        // time it's scrolled to and "Load more" is clicked. Cards appended afterwards never get
        // revealed, so they're stuck at the CSS default of opacity:0 (theme.css) - invisible, but still
        // taking up grid space, which reads as blank cells. Since these cards are appended straight into
        // view by a deliberate user action rather than discovered by scrolling, we skip the reveal
        // animation for them entirely instead of trying to re-trigger it.
        importedProductList.querySelectorAll('[reveal-on-scroll="true"]').forEach((element) => {
          element.removeAttribute('reveal-on-scroll');
        });

        currentProductList.append(...importedProductList.childNodes);

        // Same reasoning as backfillPreviousPages() above: these cards' #smartwishlist mount points
        // need a manual reinit since this fetch never dispatches theme:loading:end.
        window.ReloadSmartWishlist?.();
      }

      const currentCount = document.querySelector('[data-collection-count]');
      const newCount = parsedDocument.querySelector('[data-collection-count]');

      if (currentCount && newCount) {
        currentCount.textContent = newCount.textContent;
      }

      // <load-more-button> is now always rendered while paginate.pages > 1 (so backfillPreviousPages
      // can run on the last page too), so its presence alone no longer signals "there's a next page" -
      // check the next-url attribute itself instead.
      //
      // A section with several paginated lists (list-id set) renders one <load-more-button> per
      // list, so a plain query would grab whichever one happens to come first in the fetched
      // markup - scope it to the button carrying this same list-id instead.
      const explicitListId = this.getAttribute('list-id');
      const newButtonSelector = explicitListId ? `load-more-button[list-id="${explicitListId}"]` : 'load-more-button';
      const newNextUrl = parsedDocument.querySelector(newButtonSelector)?.getAttribute('next-url');

      if (newNextUrl) {
        this.setAttribute('next-url', newNextUrl);
      } else {
        this.remove();
      }

      const historyUrl = new URL(window.location.href);
      historyUrl.searchParams.set('page', url.searchParams.get('page') || '2');
      history.replaceState({}, '', historyUrl.toString());
    } finally {
      if (this.button) {
        this.button.disabled = false;
        this.button.removeAttribute('aria-busy');
      }
    }
  }
}

if (!window.customElements.get('load-more-button')) {
  window.customElements.define('load-more-button', LoadMoreButton);
}
